"use client"

import { useEffect, useState } from "react"
import api from "../request" // Adjust if api path is different
import {
  Bell,
  User,
  Users,
  MessageCircle,
  Calendar,
  TrendingUp,
  MapPin,
  Check,
  X,
  Send,
  Crown,
  Star,
} from "lucide-react"
import { Link } from "react-router-dom"
import Navigation from "../components/common/Navigation"
import ToastMessage from "../components/ToastMessage"

const HotStepperHub = () => {
  const [profile, setProfile] = useState(null)
  const [requests, setRequests] = useState([])
  const [activeGroups, setActiveGroups] = useState([])
  const [message, setMessage] = useState("")
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  const [toast, setToast] = useState({ message: "", type: "" })

  useEffect(() => {
    fetchProfile()
    fetchRequests()
    fetchActiveGroups()
  }, [])

  const fetchProfile = async () => {
    try {
      const res = await api.get("/hotstepper/profile")
      setProfile(res.data)
    } catch {
      setToast({ message: "Failed to fetch profile", type: "error" })
    }
  }

  const fetchRequests = async () => {
    try {
      const res = await api.get("/hotstepper/requests/incoming")
      setRequests(res.data)
    } catch {
      setToast({ message: "Failed to fetch requests", type: "error" })
    }
  }

  const fetchActiveGroups = async () => {
    try {
      const res = await api.get("/hotstepper/groups")
      setActiveGroups(res.data)
    } catch {
      setToast({ message: "Failed to fetch active groups", type: "error" })
    }
  }

  const toggleAvailability = async () => {
    await api.post("/hotstepper/toggle-availability")
    fetchProfile()
  }

  const respondToRequest = async (groupId, action) => {
    await api.post(`/hotstepper/requests/respond`, { group_id: groupId, action })
    fetchRequests()
    fetchActiveGroups()
  }

  const sendMessage = async () => {
    if (!selectedGroupId || !message) return
    await api.post("/hotstepper/message", { group_id: selectedGroupId, message })
    setMessage("")
    setToast({ message: "Message sent!", type: "success" })
  }

  const leaveGroup = async (groupId) => {
    try {
      await api.post(`/hotstepper/groups/leave`, { group_id: groupId })
      fetchActiveGroups()
      setToast({ message: "Left group successfully", type: "success" })
    } catch {
      setToast({ message: "Failed to leave group", type: "error" })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastMessage message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "" })} />

      {/* Header */}
      <header className="bg-white shadow-sm py-4 px-6 sticky top-0 z-10 lg:ml-64 border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary bg-opacity-10 rounded-lg">
              <Crown size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Hot Stepper Hub</h1>
              <p className="text-sm text-gray-600">Elite Performer Dashboard</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button className="relative p-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-all">
              <Bell size={20} />
              {requests.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {requests.length}
                </span>
              )}
            </button>
            <Link to="/settings" className="flex items-center">
              <div className="h-10 w-10 rounded-xl bg-primary text-white flex items-center justify-center hover:bg-opacity-90 transition-all">
                <User size={18} />
              </div>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 space-y-8">
        {/* Profile Overview */}
        {profile && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-primary text-white p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                  <Crown size={32} className="text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{profile.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin size={16} className="text-blue-200" />
                    <span className="text-blue-100">{profile.city}</span>
                  </div>
                  {/* <div className="flex items-center gap-1 mt-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} className="text-yellow-300 fill-current" />
                    ))}
                    <span className="text-blue-100 text-sm ml-2">Elite Performer</span>
                  </div> */}
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp size={20} className="text-blue-600" />
                    <span className="font-semibold text-blue-900">Weekly Performance</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {profile.weekly_steps?.toLocaleString() || "0"}
                    <span className="text-sm font-normal text-blue-600 ml-1">steps</span>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Users size={20} className="text-green-600" />
                    <span className="font-semibold text-green-900">Active Groups</span>
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {profile.group_count || 0}
                    <span className="text-sm font-normal text-green-600 ml-1">groups</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Incoming Group Requests */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Bell size={20} className="text-orange-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Incoming Requests</h3>
                <p className="text-gray-600 text-sm">Groups wanting you to join their team</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bell size={24} className="text-gray-400" />
                </div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">No Pending Requests</h4>
                <p className="text-gray-500">You're all caught up! New group requests will appear here.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((r) => (
                  <div
                    key={r.group_id}
                    className="border border-gray-200 rounded-xl p-5 hover:border-primary hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                            <Users size={20} className="text-primary" />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900">{r.group_name}</h4>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <MapPin size={14} />
                                <span>{r.city}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <TrendingUp size={14} />
                                <span>{r.average_steps} avg steps</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar size={12} />
                          <span>Requested {r.requested_at}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => respondToRequest(r.group_id, "accept")}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center gap-2"
                        >
                          <Check size={16} />
                          Accept
                        </button>
                        <button
                          onClick={() => respondToRequest(r.group_id, "decline")}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all flex items-center gap-2"
                        >
                          <X size={16} />
                          Decline
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Active Groups */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users size={20} className="text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Active Groups</h3>
                <p className="text-gray-600 text-sm">Groups you're currently motivating</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {activeGroups.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users size={24} className="text-gray-400" />
                </div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">No Active Groups</h4>
                <p className="text-gray-500">Accept group requests to start motivating teams!</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {activeGroups.map((g) => (
                  <div
                    key={g.id}
                    className="border border-gray-200 rounded-xl p-5 hover:border-primary hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                          <Users size={20} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">{g.name}</h4>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin size={12} />
                            <span>{g.city}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star size={14} className="text-yellow-400 fill-current" />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                      </div>
                    </div>

                    <button
                      onClick={() => leaveGroup(g.id)}
                      className="w-full px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-all font-medium"
                    >
                      Leave Group
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Message Center */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageCircle size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Message Center</h3>
                <p className="text-gray-600 text-sm">Send motivational messages to your groups</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Select Group</label>
              <select
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50"
                onChange={(e) => setSelectedGroupId(e.target.value)}
                value={selectedGroupId || ""}
              >
                <option value="">Choose a group to message</option>
                {activeGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50 resize-none"
                rows={4}
                placeholder="Type your motivational message or use emojis (🔥, 🏃, ✅, 💪)..."
              />
            </div>

            <button
              onClick={sendMessage}
              disabled={!selectedGroupId || !message}
              className="w-full bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              <Send size={18} />
              Send Message
            </button>
          </div>
        </div>
      </main>

      {/* Desktop Sidebar */}
      <nav className="hidden lg:flex fixed top-0 bottom-0 left-0 w-64 bg-white shadow flex-col">
        <Navigation />
      </nav>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden block fixed bottom-0 z-50 w-full bg-white border-t p-2">
        <Navigation orientation="horizontal" />
      </div>
    </div>
  )
}

export default HotStepperHub
